import { useState } from "react";
import { Check, Pencil, RefreshCw, X } from "lucide-react";
import { EmptyState, Panel } from "@/components/common/Primitives";
import { dateTime, num } from "@/lib/format";

export const ProfilesView = ({ profiles, onRefresh, onRename, onOpen, refreshingId }) => {
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  if (profiles.length === 0) {
    return (
      <EmptyState
        title="Nog geen zoekopdrachten"
        description="Elke uitgevoerde scan wordt automatisch als zoekopdracht bewaard, zodat je die met één klik kunt verversen."
        testid="profiles-empty"
      />
    );
  }

  return (
    <Panel
      title="Zoekopdrachten"
      subtitle="Elke scan is een bewaarde zoekopdracht met eigen marktstand"
      testid="profiles-view"
    >
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Zoekopdracht</th>
              <th>Merk / model</th>
              <th>Landen</th>
              <th className="text-right">Actief</th>
              <th className="text-right">Nieuw</th>
              <th className="text-right">Prijs verlaagd</th>
              <th className="text-right">Verdwenen</th>
              <th>Laatste verversing</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((profile) => (
              <tr key={profile.id} data-testid={`profile-row-${profile.id}`}>
                <td className="max-w-[240px]">
                  {editing === profile.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid={`profile-name-input-${profile.id}`}
                        className="h-7 w-full rounded-sm border border-slate-300 px-1.5 text-[12px] outline-none focus:border-blue-500"
                      />
                      <button
                        type="button"
                        data-testid={`profile-name-save-${profile.id}`}
                        onClick={async () => {
                          await onRename(profile.id, name);
                          setEditing(null);
                        }}
                        className="rounded-sm border border-emerald-300 p-1 text-emerald-700"
                      >
                        <Check className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        className="rounded-sm border border-slate-300 p-1 text-slate-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-[12.5px] font-semibold text-slate-900">
                        {profile.name || "Zoekopdracht"}
                      </span>
                      <button
                        type="button"
                        data-testid={`profile-rename-${profile.id}`}
                        onClick={() => {
                          setEditing(profile.id);
                          setName(profile.name || "");
                        }}
                        className="rounded-sm border border-slate-200 p-0.5 text-slate-400 hover:border-blue-400 hover:text-blue-600"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </td>
                <td className="text-[12px]">
                  {[profile.make, profile.model, profile.variant].filter(Boolean).join(" ") || "—"}
                </td>
                <td className="text-[11.5px] text-slate-600">
                  {(profile.countries || []).join(", ") || "—"}
                </td>
                <td className="num text-right font-semibold">{num(profile.active_count ?? 0)}</td>
                <td className="num text-right text-emerald-700">{num(profile.new_count ?? 0)}</td>
                <td className="num text-right text-amber-700">
                  {num(profile.price_drop_count ?? 0)}
                </td>
                <td className="num text-right text-red-700">
                  {num(profile.disappeared_count ?? 0)}
                </td>
                <td className="num text-[11.5px]">{dateTime(profile.last_run_at)}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      data-testid={`profile-refresh-${profile.id}`}
                      disabled={refreshingId === profile.id}
                      onClick={() => onRefresh(profile.id)}
                      className="flex items-center gap-1 rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      <RefreshCw
                        className={`h-3 w-3 ${refreshingId === profile.id ? "animate-spin" : ""}`}
                      />
                      Ververs
                    </button>
                    <button
                      type="button"
                      data-testid={`profile-open-${profile.id}`}
                      onClick={() => onOpen(profile.id)}
                      className="rounded-sm bg-slate-900 px-2 py-1 text-[11px] font-semibold text-white hover:bg-slate-700"
                    >
                      Voertuigen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};
